import { Link } from "react-router-dom";
import { CircleCheck, Zap, Eye, ShieldCheck, Power, Home, HardHat, ArrowRight } from "lucide-react";

const VALUES = [[CircleCheck, "Simple"], [Zap, "Powerful"], [Eye, "Transparent"], [ShieldCheck, "Reliable"], [Power, "Always On"]];

const Portal = ({ to, icon: Icon, title, text, cta, testid, primary }) => (
  <Link to={to} data-testid={testid}
    className={`group rounded-3xl p-7 backdrop-blur-xl border transition-all duration-300 hover:-translate-y-1 ${primary ? "bg-white text-[#0D131B] border-white" : "bg-white/10 text-white border-white/20 hover:bg-white/15"}`}>
    <div className="h-12 w-12 rounded-2xl flex items-center justify-center mb-5" style={{ background: "var(--grad-primary)" }}><Icon className="h-6 w-6 text-white" /></div>
    <p className="text-xl font-semibold">{title}</p>
    <p className={`text-sm mt-2 ${primary ? "text-neutral-600" : "text-neutral-300"}`}>{text}</p>
    <span className={`mt-6 inline-flex items-center gap-2 text-sm font-medium ${primary ? "text-[#FF5A00]" : "text-white"}`}>{cta}<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
  </Link>
);

export default function Landing() {
  return (
    <div className="min-h-screen relative overflow-hidden bg-[#0D131B] text-white" data-testid="landing-page">
      <img src="/brand/hero.jpg" alt="Modern home at dusk" className="absolute inset-0 h-full w-full object-cover opacity-70" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0D131B] via-[#0D131B]/80 to-[#0D131B]/20" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0D131B] via-transparent to-transparent" />
      <div className="relative z-10 min-h-screen flex flex-col max-w-7xl mx-auto px-6 sm:px-10">
        <header className="flex items-center justify-between py-6">
          <img src="/brand/logo-full-dark.png" alt="ConstructONS" className="h-10 sm:h-12" />
          <div className="flex gap-2">
            <Link to="/client-login" className="hidden sm:inline-flex btn-outline-o !bg-transparent !text-white !border-white/40 hover:!bg-white/10" data-testid="header-client-login">Client login</Link>
            <Link to="/sign-in" className="btn-grad" data-testid="header-team-login">Team login</Link>
          </div>
        </header>
        <main className="flex-1 flex flex-col justify-center py-12">
          <p className="text-xs sm:text-sm tracking-[0.3em] text-[#FF5A00] font-semibold fade-in">EVERYTHING CONSTRUCTION. ALWAYS ON.</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.08] mt-4 max-w-3xl fade-in" style={{ animationDelay: "80ms" }}>
            Dream Homes<br /><span className="grad-text">Built Better.</span>
          </h1>
          <p className="text-base sm:text-lg text-neutral-300 mt-5 max-w-xl fade-in" style={{ animationDelay: "160ms" }}>
            Follow your home from the first drawing to the day you get the keys — live site cameras, progress, photos, payments and documents in one place.
          </p>
          <div className="grid sm:grid-cols-2 gap-5 mt-10 max-w-3xl fade-in" style={{ animationDelay: "240ms" }}>
            <Portal to="/client-login" icon={Home} title="Client Dashboard" primary testid="client-login-card"
              text="For homeowners invited by ConstructONS. Watch your site live and track every milestone." cta="Sign in as client" />
            <Portal to="/sign-in" icon={HardHat} title="ConstructONS Team" testid="team-login-card"
              text="Sales, design, projects, procurement, finance and service — the business operating system." cta="Sign in as team member" />
          </div>
        </main>
        <footer className="py-8 border-t border-white/10 flex flex-wrap gap-x-10 gap-y-4 items-center justify-between">
          <div className="flex flex-wrap gap-8">{VALUES.map(([Icon, l]) => <span key={l} className="flex items-center gap-2 text-sm text-neutral-300"><Icon className="h-5 w-5 text-[#FF5A00]" />{l}</span>)}</div>
          <p className="text-xs text-neutral-500">© {new Date().getFullYear()} ConstructONS</p>
        </footer>
      </div>
    </div>
  );
}
